import constants from '@Common/Core/Constant';
import { DataParameter, IProcessDataParam } from '@Common/Core/DataServiceProvider';
import Guid from '@Common/Core/Guid';
import { dimensionFilterValue, filterValue } from '@Common/Entities/FilterEntities';
import { ActionType, EntityDriverType } from '@Common/Enums';

import { findById } from '@Common/Helpers/CollectionUtils';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import { DataTimeIntervalHelper } from '@Common/Helpers/DataTimeIntervalHelper';
import timeHelper, { dateTimeInterval } from '@Common/Helpers/TimeHelper';
import urlHelper from '@Common/Helpers/UrlHelper';
import {
    BaseDataDefinition,
    DataRequestConfiguration,
    DateTimeInterval,
    DimensionFilter,
    DimensionFilterValue,
    DimensionFilterValues,
    GroupFilter,
    MapComponent,
    MapParameter,
    ProcessConfiguration
} from '@InstanceFactory';
import { Base } from '@View/Components/Base';
import { FilterGroup, filterHelper, groupFilter } from '@View/Helpers/FilterHelper';
import { AlertMessageFilter, getAlertMessageFilter } from '@View/Core/AlertMessageFilter';
import { getRequestObject } from '@Common/Helpers/JsonUtils';

type ComponentParameter = DataRequestConfiguration;
type AnyParam = MapParameter | ComponentParameter;

export class FiltrationProvider {
    private globalFilters: GroupFilter[] = [];
    private readonly urlFilters = FiltrationProvider.getUrlFilters();

    public getGlobalFilters(): GroupFilter[] {
        return this.globalFilters;
    }

    public getUrlFilters(): GroupFilter[] {
        return this.urlFilters;
    }

    private static getUrlFilters(): GroupFilter[] {
        const dimensionFilters = [];

        const groups = [];
        const dimensions = [];
        const values = [];
        _(urlHelper.urlParameters()).forEach(pair => {
            if (pair.key.indexOf(constants.urlGroupParameterPrefix) == 0) {
                if (pair.key.indexOf(constants.urlDimensionParameterPrefix) != -1) {
                    if (pair.key.indexOf(constants.urlValueParameterPrefix) != -1) {
                        values.push(pair);
                    } else {
                        dimensions.push(pair);
                    }
                } else {
                    groups.push(pair);
                }
            }
        });
        _(groups).forEach(group => {
            let filter = _(dimensionFilters).find(item => item.groupName == group.value);
            if (filter == null) {
                filter = groupFilter({ groupName: group.value, dimensionValues: [] });
                dimensionFilters.push(filter);
            }
            _(dimensions).forEach(dimension => {
                if (dimension.key.indexOf(group.key) == 0) {
                    const filterValues = dimensionFilterValue(dimension.value, []);
                    filter.dimensionValues.push(filterValues);
                    _.forEach(values, value => {
                        if (value.key.indexOf(dimension.key) == 0) {
                            filterValues.values.push(filterValue(value.value));
                        }
                    });
                }
            });
        });

        return dimensionFilters;
    }

    public setGlobalFilter(globalFilter: GroupFilter): void {
        const existedFilter = _(this.globalFilters).find(item => item.groupName == globalFilter.groupName);
        if (existedFilter != null) {
            _(globalFilter.dimensionValues).forEach(dimensionFilterValue => {
                const existedDimensionFilterValue = _(existedFilter.dimensionValues).find(item => dimensionFilterValue.id == item.id);
                const hasNoFilterValue =
                    dimensionFilterValue.values.length == 0 || _(dimensionFilterValue.values).some(value => value.id == constants.noFilterId);
                if (existedDimensionFilterValue != null) {
                    existedFilter.dimensionValues.splice($.inArray(existedDimensionFilterValue, existedFilter.dimensionValues), 1);
                }
                if (!hasNoFilterValue) {
                    existedFilter.dimensionValues.push(dimensionFilterValue);
                }
                existedFilter.dimensionAllValuesExcluded.remove(dimensionFilterValue.id);
            });
            if (!_.isEmpty(globalFilter.dimensionAllValuesExcluded)) {
                existedFilter.dimensionAllValuesExcluded.push(...globalFilter.dimensionAllValuesExcluded);
            }
            if (globalFilter.dimensionConditions != null && globalFilter.dimensionConditions.length == 0) {
                existedFilter.dimensionConditions = null;
            } else if (globalFilter.dimensionConditions != null) {
                existedFilter.dimensionConditions = globalFilter.dimensionConditions;
            }
            _(globalFilter.parameterValues).forEach(parameterValue => {
                if (parameterValue == null) {
                    return;
                }

                const existedParameterValue = _(existedFilter.parameterValues).find(item => parameterValue.id == item.id);
                const hasNoFilterValue =
                    parameterValue.values.length == 0 || _(parameterValue.values).some(value => value.id == constants.noFilterId);
                if (existedParameterValue != null) {
                    existedFilter.parameterValues.splice($.inArray(existedParameterValue, existedFilter.parameterValues), 1);
                }
                if (!hasNoFilterValue) {
                    existedFilter.parameterValues.push(parameterValue);
                }
            });
            if (globalFilter.entityFilter) {
                if (existedFilter.entityFilter) {
                    if (globalFilter.entityFilter.type == EntityDriverType.record) {
                        existedFilter.entityFilter.recordId = globalFilter.entityFilter.recordId;
                    }
                    if (globalFilter.entityFilter.type == EntityDriverType.metric) {
                        existedFilter.entityFilter.metricId = globalFilter.entityFilter.metricId;
                    }
                } else {
                    existedFilter.entityFilter = globalFilter.entityFilter;
                }
            }
            if (globalFilter.recordsData != null) {
                if (existedFilter.recordsData == null) {
                    existedFilter.recordsData = globalFilter.recordsData;
                } else {
                    existedFilter.recordsData = filterHelper.mergeRecordsData(globalFilter.recordsData, existedFilter.recordsData);
                }
            }
            if (globalFilter.processFilters) {
                existedFilter.processFilters = filterHelper.mergeProcessFilters(globalFilter.processFilters, existedFilter.processFilters);
            }
        } else {
            globalFilter.dimensionValues = _(globalFilter.dimensionValues)
                .filter(item => item.values && item.values.length > 0 && !_(item.values).some(filterValue => filterValue.id == constants.noFilterId))
                .value();

            globalFilter.dimensionConditions = _(globalFilter.dimensionConditions)
                .filter(item => item.values && item.values.length > 0 && !_(item.values).some(filterValue => filterValue.id == constants.noFilterId))
                .value();
            if (!globalFilter.entityFilter) {
                globalFilter.entityFilter = {
                    metricId: constants.guidEmpty,
                    recordId: constants.guidEmpty
                };
            }

            this.globalFilters.push(globalFilter);
        }
    }

    public resetFilters(groupIds: string[]): void {
        const toRemove = new Set(groupIds);
        _.remove(this.globalFilters, gf => toRemove.has(gf.groupName));
    }

    public getAllGroupFilters(viewFilters: GroupFilter[]): GroupFilter[] {
        let globalDimensionFilters = this.globalFilters.slice();
        globalDimensionFilters = filterHelper.mergeGroupFiltersList(globalDimensionFilters, this.urlFilters);
        if (viewFilters) {
            globalDimensionFilters = filterHelper.mergeGroupFiltersList(globalDimensionFilters, viewFilters);
        }
        return globalDimensionFilters;
    }

    public getAllFilters(
        localFilters: GroupFilter,
        affectedDriverGroups: FilterGroup[],
        viewFilters: GroupFilter[],
        selfGroupFilter: GroupFilter,
        control?: Base<any, any>
    ): GroupFilter {
        let globalDimensionFilters = this.globalFilters;
        if (control) {
            globalDimensionFilters = commonUtils.clone(this.globalFilters);
            this.removeComponentOwnFilters(control, globalDimensionFilters);
            _(globalDimensionFilters).forEach(globalDimensionFilter => {
                globalDimensionFilter.dimensionValues = _(globalDimensionFilter.dimensionValues)
                    .filter(dimensionValue => dimensionValue.id != constants.aggregationFilterName)
                    .value();
            });
        }

        return this.getDimensionFilter(localFilters, affectedDriverGroups, viewFilters, selfGroupFilter, globalDimensionFilters);
    }

    private getDimensionFilter(localFilters, affectedDriverGroups, viewFilters, selfGroupFilter, globalDimensionFilters): GroupFilter {
        if (!selfGroupFilter) {
            selfGroupFilter = groupFilter();
        }
        let filter = groupFilter({
            groupName: null,
            dimensionValues: [],
            entityFilter: null,
            parameterValues: []
        });
        _(affectedDriverGroups).forEach(driverGroup => {
            const existedFilter = _(globalDimensionFilters).find(item => item.groupName == driverGroup.groupValue);
            if (existedFilter != null) {
                filter = filterHelper.mergeGroupFilters(filter, existedFilter);
                if (selfGroupFilter.groupName == driverGroup.groupValue) {
                    filter.dimensionValues = _(filter.dimensionValues)
                        .filter(
                            dimensionFilterValue =>
                                !_(selfGroupFilter.dimensionValues).some(
                                    selfDimensionFilterValue => selfDimensionFilterValue.id == dimensionFilterValue.id
                                )
                        )
                        .value();
                }
            }
            const urlFilter = _(this.urlFilters).find(item => item.groupName == driverGroup.groupValue);
            if (urlFilter != null) {
                filter = filterHelper.mergeGroupFilters(filter, urlFilter);
            }
            const viewFilter = _(viewFilters).find(item => item.groupName == driverGroup.groupValue);
            if (viewFilter != null) {
                filter = filterHelper.mergeGroupFilters(filter, viewFilter);
            }
        });

        return localFilters ? filterHelper.mergeGroupFilters(filter, localFilters) : filter;
    }

    public removeComponentOwnFilters(control, globalDimensionFilters, action?, dimensionIds?): void {
        if (control) {
            const actionDefinition = action ? action : control.getActionDefinition();
            if (actionDefinition && actionDefinition.actionType.hasFlag(ActionType.driverOfDimension)) {
                const outgoingGroupName = control.getOutgoingGroupName(actionDefinition);
                const groupFilter = _(globalDimensionFilters).find(globalDimensionFilter => outgoingGroupName == globalDimensionFilter.groupName);
                if (groupFilter) {
                    let filterIds;
                    if (actionDefinition.useCurrentDimension) {
                        filterIds = dimensionIds ? dimensionIds : control.getFilterIds();
                    } else {
                        filterIds = [actionDefinition.specialDimension];
                    }
                    let filterValues;
                    _(filterIds).forEach(filterId => {
                        filterValues = _(groupFilter.dimensionValues).find(filterValue => filterValue.id == filterId);
                        if (filterValues) {
                            groupFilter.dimensionValues.remove(filterValues);
                        }
                    });
                    if (groupFilter.processFilters) {
                        groupFilter.processFilters = _(groupFilter.processFilters)
                            .filter(filterValue => filterValue.componentId != control.component.id)
                            .value();
                    }
                }
            }
        }
    }

    private addSeriesFilters(dataRequestConfiguration, filters, series, parentViewIds, projectId): void {
        _(dataRequestConfiguration.seriesFilters).forEach(seriesFilter => {
            const currentSeries = _(series).find(item => item.seriesId == seriesFilter.seriesId);
            if (currentSeries != null && currentSeries.metric && currentSeries.metric.id != undefined) {
                _(currentSeries.affectedDriverGroups).forEach(group => {
                    _.forEach(parentViewIds, parentViewId => {
                        const filterGroup = filterHelper.getFilterGroup(group, parentViewId, projectId);
                        const filter = _(filters).find(filter => filter.groupName == filterGroup.groupValue);
                        if (filter != null) {
                            seriesFilter.processFilters = filterHelper.mergeProcessFilters(seriesFilter.processFilters, filter.processFilters);
                            seriesFilter.dimensionValues = filterHelper.mergeFilters(seriesFilter.dimensionValues, filter.dimensionValues);
                        }
                    });
                });
            }
        });
    }

    public needDisplayData(
        parameter: DataParameter<MapParameter | DataRequestConfiguration> | IProcessDataParam,
        projectId: string,
        shapeRequestConfiguration?
    ): boolean {
        let dataDefinition: BaseDataDefinition;
        let filters: DimensionFilter;
        if (this.isProcessDataParam(parameter)) {
            dataDefinition = parameter.configuration.componentDefinition;
            filters = parameter.configuration.filter;
        } else {
            dataDefinition = parameter.component.dataDefinition;
            const configuration = shapeRequestConfiguration ? shapeRequestConfiguration : parameter.dataRequestConfiguration;
            filters = configuration.commonFilters;
        }

        let displayData = !dataDefinition.hideDataUntilSpecialGroupFilterSet;

        if (dataDefinition.hideDataUntilSpecialGroupFilterSet) {
            const filterGroup = filterHelper.getFilterGroup(dataDefinition.specialFilterGroup, parameter.parentId, projectId);
            displayData = this.hasGroupFilter(filterGroup.groupValue) || this.hasGroupFilter(filterGroup.groupValue, parameter.viewFilters);
        }

        if (displayData && dataDefinition.hideDataUntilSpecialDimensionSet) {
            displayData = this.hasDimensionFilter(filters, dataDefinition.specialDimensionId);
            if (!displayData && parameter.viewFilters) {
                displayData = _.some(parameter.viewFilters, filter => this.hasDimensionFilter(filter, dataDefinition.specialDimensionId));
            }
        }

        return displayData;
    }

    private hasGroupFilter(groupName: string, filters: GroupFilter[] = null): boolean {
        if (!filters) {
            filters = this.globalFilters ?? [];
        }
        const groupFilter = filters.find(filter => filter.groupName == groupName);
        if (!groupFilter) {
            return false;
        }
        if (
            _.isEmpty(groupFilter.dimensionValues) &&
            _.isEmpty(groupFilter.dimensionAllValuesExcluded) &&
            _.isEmpty(groupFilter.parameterValues) &&
            _.isEmpty(groupFilter.dimensionConditions) &&
            _.isEmpty(groupFilter.processFilters)
        ) {
            return false;
        }
        return true;
    }

    private hasDimensionFilter(filter: DimensionFilter, dimension: string): boolean {
        if (filter == null) {
            return false;
        }
        return (
            _.some(filter.dimensionValues, dimensionFilterValue => dimensionFilterValue.id == dimension) ||
            _.some(filter.dimensionConditions, dimensionFilterValue => dimensionFilterValue.id == dimension) ||
            filter.dimensionAllValuesExcluded.contains(dimension)
        );
    }

    private isProcessDataParam(param: any): param is IProcessDataParam {
        return param.configuration !== undefined;
    }

    public getFilterValues(
        groupName: string,
        dimensionId: string | null,
        viewFilters: GroupFilter[],
        filterType: typeof constants.filterTypeOption.processFilter
    ): ProcessConfiguration[];
    public getFilterValues(
        groupName: string,
        dimensionId: string | null,
        viewFilters: GroupFilter[],
        filterType: typeof constants.filterTypeOption.searchPanel
    ): DimensionFilterValues[];
    public getFilterValues(
        groupName: string,
        dimensionId: string | null,
        viewFilters: GroupFilter[],
        filterType: typeof constants.filterTypeOption[keyof typeof constants.filterTypeOption]
    ): DimensionFilterValue[];
    public getFilterValues(groupName: string, dimensionId: string | null, viewFilters: GroupFilter[]): DimensionFilterValue[];
    public getFilterValues(
        groupName: string,
        dimensionId: string | null,
        viewFilters: GroupFilter[],
        filterType?: typeof constants.filterTypeOption[keyof typeof constants.filterTypeOption]
    ): DimensionFilterValues[] | DimensionFilterValue[] | ProcessConfiguration[] {
        let globalFilter = _.find(this.getAllGroupFilters(null), { groupName }) || groupFilter({ groupName });
        viewFilters = viewFilters || [];

        for (const viewFilter of viewFilters) {
            if (!groupName || viewFilter.groupName === groupName) {
                globalFilter = filterHelper.mergeGroupFilters(globalFilter, viewFilter);
            }
        }

        if (
            (dimensionId === constants.metricDriverDimensionId || dimensionId === constants.recordDriverDimensionId) &&
            globalFilter.entityFilter != null
        ) {
            const entityId =
                dimensionId === constants.metricDriverDimensionId ? globalFilter.entityFilter.metricId : globalFilter.entityFilter.recordId;
            return entityId && entityId !== Guid.empty ? [filterValue(entityId)] : [];
        } else if (filterType === constants.filterTypeOption.parameterFilter) {
            return findById(globalFilter.parameterValues, dimensionId, dimensionFilterValue(dimensionId, [])).values;
        } else if (filterType === constants.filterTypeOption.searchPanel) {
            return globalFilter.dimensionConditions;
        } else if (filterType === constants.filterTypeOption.processFilter) {
            return _.filter(globalFilter.processFilters, processFilter => processFilter.process.id === dimensionId);
        } else {
            return findById(globalFilter.dimensionValues, dimensionId, dimensionFilterValue(dimensionId, [])).values;
        }
    }

    public applyMapFilters(parameter: DataParameter<MapParameter>): void {
        let globalFilters = this.globalFilters;
        if (parameter.control) {
            globalFilters = commonUtils.clone(globalFilters);
            _((parameter.component as MapComponent).pinLayers).forEach(layer => {
                const filterIds = [layer.drillDownId];
                this.removeComponentOwnFilters(parameter.control, globalFilters, layer.appearance.actions[0], filterIds);
            });

            this.removeComponentOwnFilters(
                parameter.control,
                globalFilters,
                parameter.component.appearance.actions[0],
                parameter.dataRequestConfiguration.shapeRequestConfiguration.drillDownIds
            );
        }
        parameter.dataRequestConfiguration.shapeRequestConfiguration.commonFilters = this.getDimensionFilter(
            parameter.dataRequestConfiguration.shapeRequestConfiguration.commonFilters,
            parameter.affectedDriverGroups,
            parameter.viewFilters,
            parameter.selfGroupFilter,
            globalFilters
        );
    }

    public applyFilters(parameter: DataParameter<DataRequestConfiguration>): void {
        const requestConfiguration = parameter.dataRequestConfiguration;
        requestConfiguration.commonFilters = this.getAllFilters(
            requestConfiguration.commonFilters as GroupFilter,
            parameter.affectedDriverGroups,
            parameter.viewFilters,
            parameter.selfGroupFilter,
            parameter.control
        );
        requestConfiguration.commonFilters.recordsData = null;
        requestConfiguration.commonFilters.processFilters = getRequestObject(requestConfiguration.commonFilters.processFilters);
        this.addSeriesFilters(
            requestConfiguration,
            this.getAllGroupFilters(parameter.viewFilters),
            parameter.component.dataDefinition.series,
            parameter.parentIds,
            parameter.projectId
        );
        requestConfiguration.seriesFilters = getRequestObject(requestConfiguration.seriesFilters);
    }

    public getComponentTimeInterval(
        localFilters: GroupFilter,
        affectedDriverGroups: FilterGroup[],
        viewFilters: GroupFilter[],
        selfGroupFilter: GroupFilter
    ): DateTimeInterval {
        const filterValues = this.getAllFilters(localFilters, affectedDriverGroups, viewFilters, selfGroupFilter).dimensionValues;
        const interval = dateTimeInterval(constants.minDate, constants.maxDate);

        return this.getIntervalFromFilters(filterValues, interval);
    }

    public getIntervalFromFilters(filterValues: DimensionFilterValues[], defaultInterval: DateTimeInterval): DateTimeInterval {
        const intervals = filterValues
            .filter(value => timeHelper.isTimeDrillDown(value.id) || value.id === constants.dateTimeFilterName)
            .map(value => timeHelper.unionIntervals(_.map(value.values, value => DataTimeIntervalHelper.parse(value.id))));
        if (intervals.length > 0) {
            return timeHelper.intersectIntervals(intervals);
        }

        return defaultInterval;
    }

    public getEntityIdForGroups(groups: FilterGroup[], entityType: EntityDriverType): Guid {
        const groupValueSet = new Set(groups.map(g => g.groupValue));
        return (
            this.globalFilters
                .filter(f => !!f.entityFilter)
                .filter(f => groupValueSet.has(f.groupName))
                .map(({ entityFilter }) => (entityType === EntityDriverType.metric ? entityFilter.metricId : entityFilter.recordId))
                .find(v => Guid.isGuidValue(v)) || Guid.empty
        );
    }

    public getAggregationForGroups(groups: FilterGroup[]): any {
        const groupValueSet = new Set(groups.map(g => g.groupValue));
        return (
            this.globalFilters
                .filter(f => groupValueSet.has(f.groupName) && f.dimensionValues)
                .map(f => f.dimensionValues.find(dv => dv.id === constants.aggregationFilterName))
                .filter(av => av && av.values && av.values.length > 0)
                .map(av => av.values[0].id)
                .find(Boolean) || null
        );
    }

    public setGlobalFilters(filters: GroupFilter[]): void {
        this.globalFilters = filters;
    }

    public getAlertMessageFilter(groups: FilterGroup[], projectId: Guid): AlertMessageFilter {
        const groupValueSet = new Set(groups.map(g => g.groupValue));
        const filters = this.globalFilters.filter(f => groupValueSet.has(f.groupName));
        if (filters.length === 0) {
            return _.stubTrue;
        }

        return getAlertMessageFilter(filters, projectId);
    }
}
